import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    PRODUCTS,
    PRODUCT_CATEGORIES,
    SOLUTIONS_BY_INDUSTRY,
    SOLUTIONS_BY_CAPABILITY,
    ProductItem,
    SolutionItem,
    launchUrl,
} from "../../data/productsData";
import { loadProducts } from "../../services/productSource";
import { loadSolutions } from "../../services/platformSource";
import "../../styles/revamp.css";

/**
 * The two header mega-menus: Platform and Solutions.
 *
 * They read from the product registry rather than a hand-maintained list, so
 * adding a product to productsData.ts puts it in the navigation, on the
 * homepage tabs, and in the catalog at once — there is no second place to
 * forget.
 *
 * Opens on hover for pointer users and on click/Enter for everyone, because a
 * hover-only mega-menu is unreachable by keyboard and unusable on touch.
 */

type MenuKey = "platform" | "solutions" | null;

/** Mega-menu columns. The registry's four categories collapse into the three
 *  groups the business actually sells against — Cloud and CRM are one
 *  conversation for a buyer even though they are different codebases. */
const PLATFORM_GROUPS: { title: string; categories: typeof PRODUCT_CATEGORIES }[] = [
    { title: "AI & autonomous agents", categories: ["AI Agents"] },
    { title: "Computer vision", categories: ["Computer Vision"] },
    { title: "Cloud, messaging & business", categories: ["DevOps & Cloud", "Messaging & Comms", "Hospitality & CRM"] },
];

const useDismiss = (onDismiss: () => void, active: boolean) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!active) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onDismiss();
        };
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
        };

        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClick);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClick);
        };
    }, [active, onDismiss]);

    return ref;
};

const ProductLink: React.FC<{ product: ProductItem; onNavigate: () => void }> = ({
    product,
    onNavigate,
}) => (
    <li>
        <Link className="mx-panel-link" to={`/products/${product.slug}`} onClick={onNavigate}>
            <span className="mx-panel-link-name">
                <span className="mx-dot" style={{ ["--mx-dot-color" as any]: product.accent }} />
                {product.name}
            </span>
            <span className="mx-panel-link-desc">{product.tagline}</span>
        </Link>
    </li>
);

const PlatformNav: React.FC = () => {
    const [open, setOpen] = useState<MenuKey>(null);
    const close = () => setOpen(null);
    const ref = useDismiss(close, open !== null);
    const idBase = useId();

    const toggle = (key: Exclude<MenuKey, null>) => setOpen((cur) => (cur === key ? null : key));

    // Hover is a convenience for pointer users; click is the real control, so
    // keyboard and touch reach the same panels.
    const hoverProps = (key: Exclude<MenuKey, null>) => ({
        onMouseEnter: () => setOpen(key),
        onMouseLeave: close,
    });

    const [products, setProducts] = useState<ProductItem[]>(PRODUCTS);
    const [solutions, setSolutions] = useState<{ industry: SolutionItem[]; capability: SolutionItem[] }>({
        industry: SOLUTIONS_BY_INDUSTRY,
        capability: SOLUTIONS_BY_CAPABILITY,
    });

    useEffect(() => {
        let cancelled = false;
        loadProducts().then((loaded) => {
            if (!cancelled) setProducts(loaded);
        });
        loadSolutions().then((loaded) => {
            if (!cancelled) setSolutions(loaded);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const featured = products[0] ?? PRODUCTS[0];

    return (
        // display:contents keeps the <li> children as direct children of the
        // header <ul> for layout, while still giving useDismiss an element to
        // test clicks against. The hide-on-mobile class goes on each <li>, not
        // here — an inline display would override the media query.
        <div ref={ref} style={{ display: "contents" }}>
            {/* Platform */}
            <li className="mx-nav mx-nav-desktop-only" {...hoverProps("platform")}>
                <button
                    type="button"
                    className="mx-nav-trigger"
                    aria-expanded={open === "platform"}
                    aria-controls={`${idBase}-platform`}
                    onClick={() => toggle("platform")}
                >
                    Platform <span className="mx-nav-caret" aria-hidden="true" />
                </button>

                <div
                    id={`${idBase}-platform`}
                    className="mx-panel mx-panel-platform"
                    data-open={open === "platform"}
                >
                    {PLATFORM_GROUPS.map((group) => (
                        <div key={group.title}>
                            <p className="mx-panel-group-title">{group.title}</p>
                            <ul className="mx-panel-list">
                                {products.filter((p) => group.categories.includes(p.category)).map((p) => (
                                    <ProductLink key={p.slug} product={p} onNavigate={close} />
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="mx-panel-feature">
                        <p className="mx-eyebrow">In focus</p>
                        <p className="mx-panel-link-name">{featured.name}</p>
                        <p className="mx-panel-feature-body">{featured.tagline}</p>
                        {launchUrl(featured) && (
                            <a
                                className="mx-launch"
                                href={launchUrl(featured)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open ${featured.name} in a new tab`}
                            >
                                Launch app ↗
                            </a>
                        )}
                    </div>
                </div>
            </li>

            {/* Solutions */}
            <li className="mx-nav mx-nav-desktop-only" {...hoverProps("solutions")}>
                <button
                    type="button"
                    className="mx-nav-trigger"
                    aria-expanded={open === "solutions"}
                    aria-controls={`${idBase}-solutions`}
                    onClick={() => toggle("solutions")}
                >
                    Solutions <span className="mx-nav-caret" aria-hidden="true" />
                </button>

                <div
                    id={`${idBase}-solutions`}
                    className="mx-panel mx-panel-solutions"
                    data-open={open === "solutions"}
                >
                    <div>
                        <p className="mx-panel-group-title">By industry</p>
                        <ul className="mx-panel-list">
                            {solutions.industry.map((s) => (
                                <li key={s.slug}>
                                    <Link className="mx-panel-link" to={`/products?solution=${s.slug}`} onClick={close}>
                                        <span className="mx-panel-link-name">{s.title}</span>
                                        <span className="mx-panel-link-desc">{s.description}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="mx-panel-group-title">By capability</p>
                        <ul className="mx-panel-list">
                            {solutions.capability.map((s) => (
                                <li key={s.slug}>
                                    <Link className="mx-panel-link" to={`/products?solution=${s.slug}`} onClick={close}>
                                        <span className="mx-panel-link-name">{s.title}</span>
                                        <span className="mx-panel-link-desc">{s.description}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </li>

        </div>
    );
};

export default PlatformNav;
